/**
 * Large Media Rows — Apple-style sticky media rail.
 *
 * Desktop (>=990px): the media column is pinned (CSS sticky) while the text
 * column scrolls. The media whose text block is crossing the viewport centre
 * is faded in; the rest fade out (crossfade is CSS; this just toggles
 * `.is-active`). Only the active video plays.
 *
 * Mobile (<990px): media and text are stacked per block (no rail). Every media
 * is shown and its video plays while in view.
 *
 * Respects prefers-reduced-motion: videos are never auto-played when it is on.
 */
class LargeMediaRows extends HTMLElement {
  connectedCallback() {
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.texts = Array.from(this.querySelectorAll('.large-media-rows__text-inner[data-index]'));
    this.medias = Array.from(this.querySelectorAll('.large-media-rows__media[data-index]'));
    this.videos = Array.from(this.querySelectorAll('.large-media-rows__video'));
    this.activeIndex = 0;

    this.videos.forEach((video) => {
      video.muted = true;
      video.loop = true;
      video.setAttribute('playsinline', '');
    });

    // Track scroll direction so the desktop rail can use a different media-swap
    // trigger line going up vs. down (see startRail).
    this.lastScrollY = window.scrollY;
    this.scrollDir = 'down';
    this.onScroll = () => {
      const y = window.scrollY;
      if (y > this.lastScrollY) this.scrollDir = 'down';
      else if (y < this.lastScrollY) this.scrollDir = 'up';
      this.lastScrollY = y;
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.handleChange = this.handleChange.bind(this);
    this.desktop.addEventListener('change', this.handleChange);
    this.reducedMotion.addEventListener('change', this.handleChange);

    this.handleChange();
  }

  handleChange() {
    this.teardown();
    if (this.desktop.matches) {
      this.startRail();
    } else {
      this.startStacked();
    }
  }

  /* Desktop: a text becomes active when its content crosses a horizontal trigger
     line. The rootMargin collapses the root to a 0-height line at that height, so
     only the content overlapping the line reports as intersecting.

     Two lines are used so the swap point differs by scroll direction: scrolling
     down swaps late (near the bottom third), scrolling up swaps earlier (near the
     top). The gap between them forms a hysteresis band that also avoids flicker. */
  startRail() {
    // % down from the top of the viewport where the media swaps.
    // Down-scroll swaps late (near bottom third); up-scroll swaps earlier (near top).
    const triggerDownFromTop = 75;
    const triggerUpFromTop = 33;

    const makeObserver = (triggerFromTop, direction) =>
      new IntersectionObserver(
        (entries) => {
          if (this.scrollDir !== direction) return; // only act in its own direction
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.activate(Number(entry.target.dataset.index));
          });
        },
        { rootMargin: `-${triggerFromTop}% 0px -${100 - triggerFromTop}% 0px`, threshold: 0 }
      );

    this.railObserverDown = makeObserver(triggerDownFromTop, 'down');
    this.railObserverUp = makeObserver(triggerUpFromTop, 'up');
    this.texts.forEach((text) => {
      this.railObserverDown.observe(text);
      this.railObserverUp.observe(text);
    });

    // Before the first text reaches the trigger, keep one shown.
    this.activate(this.activeIndex);
  }

  activate(index) {
    this.activeIndex = index;
    this.medias.forEach((media) => {
      const isActive = Number(media.dataset.index) === index;
      media.classList.toggle('is-active', isActive);

      const video = media.querySelector('.large-media-rows__video');
      if (!video) return;
      if (isActive && !this.reducedMotion.matches) {
        const playPromise = video.play();
        if (playPromise !== undefined) playPromise.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  /* Mobile: every media is visible in flow; play each video while it is in view. */
  startStacked() {
    this.medias.forEach((media) => media.classList.add('is-active'));

    this.stackedObserver = new IntersectionObserver(
      (entries) => {
        if (this.reducedMotion.matches) return;
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            const playPromise = video.play();
            if (playPromise !== undefined) playPromise.catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    this.videos.forEach((video) => this.stackedObserver.observe(video));
  }

  teardown() {
    if (this.railObserverDown) {
      this.railObserverDown.disconnect();
      this.railObserverDown = null;
    }
    if (this.railObserverUp) {
      this.railObserverUp.disconnect();
      this.railObserverUp = null;
    }
    if (this.stackedObserver) {
      this.stackedObserver.disconnect();
      this.stackedObserver = null;
    }
    this.videos.forEach((video) => video.pause());
  }

  disconnectedCallback() {
    this.teardown();
    window.removeEventListener('scroll', this.onScroll);
    this.desktop.removeEventListener('change', this.handleChange);
    this.reducedMotion.removeEventListener('change', this.handleChange);
  }
}

customElements.define('large-media-rows', LargeMediaRows);
